import { useEffect, useState } from "react";
import { CAMPO, Recado } from "./ui";
import { mensagemDoCatalogo } from "../lib/adminCatalogo";
import {
  enviarDocumento,
  lerDocumentosDoProduto,
  linkDoDocumento,
  retirarDocumento,
  salvarTipoDeDocumento,
  type DocumentoDoProduto,
  type DocumentosDoProduto,
  type TipoDeDocumento,
} from "../lib/documentosDoProduto";

/**
 * Admin › Catálogo › Produtos › o produto › **Documentos do produto** (000306 —
 * o G5 do Plano de Melhorias).
 *
 * A ementa, o projeto pedagógico e os demais documentos do curso moram aqui, e
 * não mais no Drive. A MATRIZ (quais documentos existem, se são obrigatórios, o
 * vínculo) é da equipe de Produtos e vale para todos os produtos; os documentos
 * são de cada curso, e cada envio de um que já existe é uma VERSÃO nova — a
 * anterior fica no histórico, e trocar pede o porquê.
 *
 * "A definir" é um estado, e a tela o diz com letra: o plano manda o campo
 * ficar assim onde o time não decidiu, e mostrá-lo como "não" seria decidir por
 * ele.
 */

const OBRIGATORIO = (v: boolean | null) =>
  v === true ? "obrigatório" : v === false ? "opcional" : "obrigatoriedade a definir";
const VINCULO = (v: TipoDeDocumento["vinculo"]) =>
  v === "produto" ? "do produto" : v === "turma" ? "de cada turma" : v === "ambos" ? "do produto ou da turma" : "vínculo a definir";

function tamanho(bytes: number | null): string {
  if (bytes == null) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} kB`;
  return `${(bytes / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}
const data = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });

type Envio = { tipoId: string; turmaId: string; titulo: string; motivo: string; arquivo: File | null };

export default function AdminCatalogoProdutoDocumentos({
  curso,
  aoMudar,
}: {
  curso: string;
  /** Avisa o pai que um documento ou a matriz mudou — o histórico do produto relê. */
  aoMudar?: () => void;
}) {
  const [dados, setDados] = useState<DocumentosDoProduto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  // Um ato aberto por vez: dois formulários na mesma lista fariam a pessoa
  // confirmar um achando que confirmava o outro.
  const [enviando, setEnviando] = useState<Envio | null>(null);
  const [retirando, setRetirando] = useState<{ id: string; motivo: string } | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerDocumentosDoProduto(curso);
      if (!vivo) return;
      if (r.ok) {
        setDados(r);
        setErro(null);
      } else {
        setErro(mensagemDoCatalogo(r));
      }
    })();
    return () => {
      vivo = false;
    };
  }, [curso, versao]);

  async function baixar(d: DocumentoDoProduto) {
    // A url é assinada no CLIQUE, por um minuto: o bucket é privado, e assinar a
    // lista inteira ao desenhar gastaria uma chamada por arquivo que ninguém abre.
    const url = await linkDoDocumento(d.arquivo_path, d.nome_arquivo);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else setRecado({ tipo: "erro", texto: "Não foi possível gerar o link do arquivo — tente de novo." });
  }

  async function enviar() {
    if (!enviando) return;
    if (!enviando.arquivo) {
      setRecado({ tipo: "erro", texto: "Escolha o arquivo do documento." });
      return;
    }
    setSalvando(true);
    setRecado(null);
    try {
      const r = await enviarDocumento({
        curso,
        tipoId: enviando.tipoId,
        turmaId: enviando.turmaId || null,
        titulo: enviando.titulo,
        arquivo: enviando.arquivo,
        motivo: enviando.motivo.trim() || null,
      });
      setRecado({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) {
        setEnviando(null);
        setVersao((v) => v + 1);
        aoMudar?.();
      }
    } finally {
      setSalvando(false);
    }
  }

  async function retirar() {
    if (!retirando) return;
    setSalvando(true);
    setRecado(null);
    try {
      const r = await retirarDocumento(retirando.id, retirando.motivo.trim());
      setRecado({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) {
        setRetirando(null);
        setVersao((v) => v + 1);
        aoMudar?.();
      }
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-documentos" data-documentos-do-produto>
      <h3 id="titulo-documentos" className="font-display text-lg font-bold text-titulo">
        Documentos do produto
      </h3>
      <p className="mt-1 text-sm text-cinza">
        A ementa, o projeto pedagógico e os demais documentos do curso moram aqui, e não mais no
        Drive. Enviar um documento que já existe cria a versão seguinte: a anterior fica no
        histórico, e trocar pede o porquê. Quem vê o Catálogo baixa; quem edita envia.
      </p>

      {erro && <Recado tipo="erro" texto={erro} />}
      {!dados && !erro && <p className="mt-3 text-sm text-cinza">Carregando…</p>}

      {dados && (
        <>
          {dados.faltando.length > 0 && (
            <p className="mt-4 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo px-3 py-2 text-sm text-alerta-tinta" data-faltando>
              Obrigatório sem documento: {dados.faltando.join(", ")}.
            </p>
          )}

          <ul className="mt-4 divide-y divide-borda">
            {dados.tipos.map((t) => {
              const doTipo = dados.documentos.filter((d) => d.tipo_id === t.id);
              const vigentes = doTipo.filter((d) => d.vigente);
              const antigos = doTipo.filter((d) => !d.vigente);
              const vigenteDaTurma = (turmaId: string) =>
                vigentes.find((d) => (d.turma_id ?? "") === turmaId) ?? null;
              return (
                <li key={t.id} className="py-4" data-tipo-documento={t.nome}>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h4 className="font-semibold text-tinta">{t.nome}</h4>
                    <span className="text-xs text-cinza">
                      {t.categoria} · {OBRIGATORIO(t.obrigatorio)} · {VINCULO(t.vinculo)}
                    </span>
                    {!t.ativo && (
                      <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta">
                        fora da matriz
                      </span>
                    )}
                  </div>

                  {vigentes.length === 0 ? (
                    <p className="mt-1.5 text-sm text-cinza">Nenhum documento vigente.</p>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {vigentes.map((d) => (
                        <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-documento-vigente={d.id}>
                          <span className="rounded-full bg-aviso-fundo px-2 py-0.5 text-xs font-semibold text-aviso-tinta">
                            versão {d.versao}
                          </span>
                          <span className="font-semibold text-tinta">{d.titulo}</span>
                          {d.turma_numero != null && <span className="text-cinza">turma {d.turma_numero}</span>}
                          <span className="text-xs text-cinza">
                            {d.nome_arquivo}
                            {d.tamanho_bytes != null && ` · ${tamanho(d.tamanho_bytes)}`} · enviado em {data(d.enviado_em)} por{" "}
                            {d.enviado_por}
                          </span>
                          <button
                            type="button"
                            data-baixar-documento={d.id}
                            onClick={() => void baixar(d)}
                            className="text-xs font-semibold text-link underline-offset-2 hover:underline"
                          >
                            Baixar
                          </button>
                          <button
                            type="button"
                            data-retirar-documento={d.id}
                            onClick={() => {
                              setRecado(null);
                              setEnviando(null);
                              setRetirando({ id: d.id, motivo: "" });
                            }}
                            className="text-xs font-semibold text-cinza underline-offset-2 hover:text-tinta hover:underline"
                          >
                            Retirar
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {t.ativo && enviando?.tipoId !== t.id && (
                    <button
                      type="button"
                      data-enviar-documento={t.nome}
                      onClick={() => {
                        setRecado(null);
                        setRetirando(null);
                        setEnviando({ tipoId: t.id, turmaId: "", titulo: "", motivo: "", arquivo: null });
                      }}
                      className="btn-contorno mt-3 min-h-9 text-xs"
                    >
                      {vigentes.length > 0 ? "Enviar nova versão" : "Enviar documento"}
                    </button>
                  )}

                  {enviando?.tipoId === t.id && (
                    <div className="mt-3 rounded-cf border border-borda p-3" data-form-envio={t.nome}>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="flex flex-col gap-1.5 sm:col-span-2">
                          <span className="text-sm font-semibold text-tinta">Arquivo</span>
                          <input
                            type="file"
                            data-arquivo-documento
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                            className="text-sm text-tinta"
                            onChange={(e) => {
                              const f = e.target.files?.[0] ?? null;
                              setEnviando((x) =>
                                x ? { ...x, arquivo: f, titulo: x.titulo || (f ? f.name.replace(/\.[^.]+$/, "") : "") } : x,
                              );
                            }}
                          />
                          <span className="text-xs text-cinza">PDF, Word, Excel, PowerPoint ou imagem, até 50 MB.</span>
                        </label>
                        <label className="flex flex-col gap-1.5">
                          <span className="text-sm font-semibold text-tinta">Título</span>
                          <input
                            data-campo-documento="titulo"
                            className={CAMPO}
                            value={enviando.titulo}
                            onChange={(e) => setEnviando({ ...enviando, titulo: e.target.value })}
                          />
                        </label>
                        {t.vinculo !== "produto" && dados.turmas.length > 0 && (
                          <label className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-tinta">Turma</span>
                            <select
                              data-campo-documento="turma"
                              className={CAMPO}
                              value={enviando.turmaId}
                              onChange={(e) => setEnviando({ ...enviando, turmaId: e.target.value })}
                            >
                              {t.vinculo !== "turma" && <option value="">Do produto (todas as turmas)</option>}
                              {t.vinculo === "turma" && <option value="">Escolha a turma</option>}
                              {dados.turmas.map((tu) => (
                                <option key={tu.id} value={tu.id}>
                                  Turma {tu.numero}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        <label className="flex flex-col gap-1.5 sm:col-span-2">
                          <span className="text-sm font-semibold text-tinta">Justifique a alteração</span>
                          <input
                            data-campo-documento="motivo"
                            className={CAMPO}
                            value={enviando.motivo}
                            onChange={(e) => setEnviando({ ...enviando, motivo: e.target.value })}
                            placeholder="Vai para o histórico do produto"
                          />
                          {/* O aviso ANTES do clique: quem decide que é obrigatório
                              é o banco, e a recusa vem com a frase dele. */}
                          {vigenteDaTurma(enviando.turmaId) && (
                            <span className="text-xs text-alerta-tinta" data-substitui-versao>
                              Já existe a versão {vigenteDaTurma(enviando.turmaId)!.versao}: o motivo é obrigatório,
                              e ela continua no histórico.
                            </span>
                          )}
                        </label>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          data-confirmar-envio
                          disabled={salvando}
                          onClick={() => void enviar()}
                          className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55"
                        >
                          {salvando ? "Enviando…" : "Enviar"}
                        </button>
                        <button type="button" onClick={() => setEnviando(null)} className="btn-contorno min-h-10 text-sm">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {retirando && vigentes.some((d) => d.id === retirando.id) && (
                    <div className="mt-3 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo p-3" data-form-retirada-documento>
                      <p className="text-sm text-alerta-tinta">
                        Retirar este documento. Ele sai de vigente e fica no histórico, com o motivo.
                      </p>
                      <label className="mt-3 flex flex-col gap-1.5">
                        <span className="text-sm font-semibold text-alerta-tinta">Justifique a alteração</span>
                        <input
                          data-campo-documento="motivo-retirada"
                          className={CAMPO}
                          value={retirando.motivo}
                          onChange={(e) => setRetirando({ ...retirando, motivo: e.target.value })}
                        />
                      </label>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {/* Sem `disabled` por motivo vazio: quem decide é o BANCO. */}
                        <button
                          type="button"
                          data-confirmar-retirada-documento
                          disabled={salvando}
                          onClick={() => void retirar()}
                          className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55"
                        >
                          {salvando ? "Retirando…" : "Retirar"}
                        </button>
                        <button type="button" onClick={() => setRetirando(null)} className="btn-contorno min-h-10 text-sm">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {antigos.length > 0 && (
                    <details className="mt-3" data-historico-do-documento={t.nome}>
                      <summary className="cursor-pointer text-xs font-semibold text-cinza">
                        Histórico — {antigos.length} versão(ões) anterior(es)
                      </summary>
                      <ul className="mt-2 space-y-1.5">
                        {antigos.map((d) => (
                          <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-cinza" data-versao-antiga={d.versao}>
                            <span className="font-semibold text-tinta">versão {d.versao}</span>
                            <span>{d.titulo}</span>
                            {d.turma_numero != null && <span>turma {d.turma_numero}</span>}
                            <span>
                              enviada em {data(d.enviado_em)} por {d.enviado_por}
                              {d.retirado_em
                                ? ` · retirada em ${data(d.retirado_em)} por ${d.retirado_por ?? "—"}: ${d.motivo_retirada}`
                                : " · substituída pela seguinte"}
                            </span>
                            <button
                              type="button"
                              onClick={() => void baixar(d)}
                              className="font-semibold text-link underline-offset-2 hover:underline"
                            >
                              Baixar
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </li>
              );
            })}
          </ul>

          {recado && <div data-recado-documentos><Recado tipo={recado.tipo} texto={recado.texto} /></div>}

          <MatrizDeDocumentos
            matriz={dados.matriz}
            aoMudar={() => {
              setVersao((v) => v + 1);
              aoMudar?.();
            }}
          />
        </>
      )}
    </section>
  );
}

type LinhaDaMatriz = {
  id: string | null;
  nome: string;
  categoria: string;
  obrigatorio: "" | "sim" | "nao";
  vinculo: "" | "produto" | "turma" | "ambos";
  versionado: "" | "sim" | "nao";
  ordem: string;
  ativo: boolean;
  motivo: string;
};

const deTipo = (t: TipoDeDocumento): LinhaDaMatriz => ({
  id: t.id,
  nome: t.nome,
  categoria: t.categoria,
  obrigatorio: t.obrigatorio === true ? "sim" : t.obrigatorio === false ? "nao" : "",
  vinculo: t.vinculo ?? "",
  versionado: t.versionado === true ? "sim" : t.versionado === false ? "nao" : "",
  ordem: String(t.ordem),
  ativo: t.ativo,
  motivo: "",
});
const triplo = (v: "" | "sim" | "nao") => (v === "sim" ? true : v === "nao" ? false : null);

/**
 * A MATRIZ DOCUMENTAL, editável pela equipe — e ela vale para TODOS os
 * produtos, o que a tela diz antes de qualquer campo. Uma linha editada por
 * vez; desativar pede o porquê (o banco cobra), e o tipo desativado continua na
 * lista, para poder voltar.
 */
function MatrizDeDocumentos({ matriz, aoMudar }: { matriz: TipoDeDocumento[]; aoMudar: () => void }) {
  const [editando, setEditando] = useState<LinhaDaMatriz | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  async function salvar() {
    if (!editando) return;
    setSalvando(true);
    setRecado(null);
    try {
      const r = await salvarTipoDeDocumento({
        id: editando.id,
        nome: editando.nome,
        categoria: editando.categoria,
        obrigatorio: triplo(editando.obrigatorio),
        vinculo: editando.vinculo || null,
        versionado: triplo(editando.versionado),
        ordem: Number(editando.ordem) || 100,
        ativo: editando.ativo,
        motivo: editando.motivo.trim() || null,
      });
      setRecado({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) {
        setEditando(null);
        aoMudar();
      }
    } finally {
      setSalvando(false);
    }
  }

  return (
    <details className="mt-6 border-t border-borda pt-4" data-matriz-de-documentos>
      <summary className="cursor-pointer text-sm font-semibold text-tinta">
        A matriz de documentos — {matriz.filter((t) => t.ativo).length} tipo(s)
      </summary>
      <p className="mt-2 text-sm text-cinza">
        Quais documentos existem, a categoria, se são obrigatórios e se são do produto ou de cada
        turma. <strong className="text-tinta">Vale para todos os produtos.</strong> Onde o time
        ainda não decidiu, o campo fica «a definir» — e o que falta só conta os obrigatórios.
      </p>

      <ul className="mt-3 divide-y divide-borda">
        {matriz.map((t) => (
          <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm" data-linha-da-matriz={t.nome}>
            <span className={`font-semibold ${t.ativo ? "text-tinta" : "text-cinza line-through"}`}>{t.nome}</span>
            <span className="text-xs text-cinza">
              {t.categoria} · {OBRIGATORIO(t.obrigatorio)} · {VINCULO(t.vinculo)} ·{" "}
              {t.versionado === true ? "versionado" : t.versionado === false ? "sem versão" : "versão a definir"}
              {!t.ativo && " · desativado"}
            </span>
            <button
              type="button"
              data-editar-tipo={t.nome}
              onClick={() => {
                setRecado(null);
                setEditando(deTipo(t));
              }}
              className="text-xs font-semibold text-link underline-offset-2 hover:underline"
            >
              Editar
            </button>
          </li>
        ))}
      </ul>

      {!editando && (
        <button
          type="button"
          data-novo-tipo
          onClick={() => {
            setRecado(null);
            setEditando({
              id: null, nome: "", categoria: "", obrigatorio: "", vinculo: "", versionado: "",
              ordem: String(10 * (matriz.length + 1)), ativo: true, motivo: "",
            });
          }}
          className="btn-contorno mt-3 min-h-9 text-xs"
        >
          + Documento na matriz
        </button>
      )}

      {editando && (
        <div className="mt-3 rounded-cf border border-borda p-3" data-form-tipo>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-semibold text-tinta">Documento</span>
              <input data-campo-tipo="nome" className={CAMPO} value={editando.nome}
                onChange={(e) => setEditando({ ...editando, nome: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Categoria</span>
              <input data-campo-tipo="categoria" className={CAMPO} value={editando.categoria} placeholder="Acadêmico"
                onChange={(e) => setEditando({ ...editando, categoria: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Obrigatório?</span>
              <select data-campo-tipo="obrigatorio" className={CAMPO} value={editando.obrigatorio}
                onChange={(e) => setEditando({ ...editando, obrigatorio: e.target.value as LinhaDaMatriz["obrigatorio"] })}>
                <option value="">A definir</option>
                <option value="sim">Sim</option>
                <option value="nao">Não</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Vínculo</span>
              <select data-campo-tipo="vinculo" className={CAMPO} value={editando.vinculo}
                onChange={(e) => setEditando({ ...editando, vinculo: e.target.value as LinhaDaMatriz["vinculo"] })}>
                <option value="">A definir</option>
                <option value="produto">Do produto</option>
                <option value="turma">De cada turma</option>
                <option value="ambos">Produto ou turma</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Versionado?</span>
              <select data-campo-tipo="versionado" className={CAMPO} value={editando.versionado}
                onChange={(e) => setEditando({ ...editando, versionado: e.target.value as LinhaDaMatriz["versionado"] })}>
                <option value="">A definir</option>
                <option value="sim">Sim</option>
                <option value="nao">Não</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Ordem</span>
              <input data-campo-tipo="ordem" inputMode="numeric" className={CAMPO} value={editando.ordem}
                onChange={(e) => setEditando({ ...editando, ordem: e.target.value })} />
            </label>
            {editando.id && (
              <label className="flex items-center gap-2 self-end pb-2 text-sm text-tinta">
                <input type="checkbox" data-campo-tipo="ativo" checked={editando.ativo}
                  onChange={(e) => setEditando({ ...editando, ativo: e.target.checked })} />
                Na matriz (ativo)
              </label>
            )}
            <label className="flex flex-col gap-1.5 sm:col-span-3">
              <span className="text-sm font-semibold text-tinta">Justifique a alteração</span>
              <input data-campo-tipo="motivo" className={CAMPO} value={editando.motivo}
                onChange={(e) => setEditando({ ...editando, motivo: e.target.value })}
                placeholder="Obrigatório para tirar da matriz" />
            </label>
          </div>
          <p className="mt-2 text-xs text-cinza">
            Os documentos já enviados continuam no histórico de cada produto, mesmo que este tipo saia
            da matriz. Versionar não muda o que a tela guarda: toda versão fica.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" data-salvar-tipo disabled={salvando} onClick={() => void salvar()}
              className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55">
              {salvando ? "Salvando…" : editando.id ? "Salvar na matriz" : "Pôr na matriz"}
            </button>
            <button type="button" onClick={() => setEditando(null)} className="btn-contorno min-h-10 text-sm">
              Cancelar
            </button>
          </div>
        </div>
      )}
      {recado && <div data-recado-matriz><Recado tipo={recado.tipo} texto={recado.texto} /></div>}
    </details>
  );
}
